import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Clock, DollarSign, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * TimeDetailsPanel component - Displays detailed time entry information for a task
 *
 * @param {Array} timeEntries - Array of time entries for the task
 * @param {boolean} showDetails - Whether to initially show the details panel
 */
const TimeDetailsPanel = ({ 
  timeEntries = [], 
  showDetails = false,
  taskId
}) => {
  const [isExpanded, setIsExpanded] = useState(showDetails);

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Calculate duration from start and end time
  const calculateDuration = (entry) => {
    if (entry.duration_minutes) {
      const hours = Math.floor(entry.duration_minutes / 60);
      const minutes = entry.duration_minutes % 60;
      return `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}`;
    }

    if (!entry.end_time) {
      return 'In progress';
    }

    const start = new Date(entry.start_time);
    const end = new Date(entry.end_time);
    const durationMs = end - start;
    const durationMinutes = Math.round(durationMs / (1000 * 60));
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    
    return `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}`;
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'running':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'completed':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      default:
        return 'bg-grayteck-100 text-grayteck-800 dark:bg-grayteck-900/30 dark:text-grayteck-300';
    }
  };

  // No entries
  if (timeEntries.length === 0) {
    return null;
  }

  // Calculate total time
  const totalTime = timeEntries.reduce((total, entry) => {
    if (!entry.end_time) return total;
    
    if (entry.duration_minutes) {
      return total + entry.duration_minutes;
    }
    
    const start = new Date(entry.start_time);
    const end = new Date(entry.end_time);
    const durationMs = end - start;
    const durationMinutes = Math.round(durationMs / (1000 * 60));
    
    return total + durationMinutes;
  }, 0);
  
  const totalHours = Math.floor(totalTime / 60);
  const totalMinutes = totalTime % 60;
  const totalFormatted = `${totalHours}h${totalMinutes > 0 ? ` ${totalMinutes}m` : ''}`;

  return (
    <div className="mt-2 border-t border-grayteck-200 dark:border-grayteck-700 pt-2">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between py-1 px-2 hover:bg-grayteck-100 dark:hover:bg-darkbg-700 rounded-md transition-colors"
      >
        <div className="flex items-center text-xs text-grayteck-600 dark:text-grayteck-400">
          <Clock className="w-3 h-3 mr-1" />
          <span>{timeEntries.length} time entries</span>
          <span className="mx-1">·</span>
          <span>Total: {totalFormatted}</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-grayteck-500 dark:text-grayteck-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-grayteck-500 dark:text-grayteck-400" />
        )}
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-2 space-y-2 px-2">
              {timeEntries.map((entry) => (
                <div
                  key={entry.id}
                  className="p-2 bg-white dark:bg-darkbg-800 border border-grayteck-200 dark:border-grayteck-700 rounded-md"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center">
                        <span className="text-xs text-grayteck-500 dark:text-grayteck-400">
                          {formatDate(entry.start_time)}
                        </span>
                        <span className="mx-1 text-xs text-grayteck-400 dark:text-grayteck-500">→</span>
                        <span className="text-xs text-grayteck-500 dark:text-grayteck-400">
                          {entry.end_time ? formatDate(entry.end_time) : 'In progress'}
                        </span>
                      </div>
                      
                      <p className="text-sm text-grayteck-700 dark:text-grayteck-300 mt-1">
                        {entry.description || <span className="italic text-grayteck-500 dark:text-grayteck-500">No description</span>}
                      </p>
                    </div>

                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusBadgeClass(entry.status)}`}>
                        {entry.status}
                      </span>
                      <span className="text-sm font-medium text-grayteck-800 dark:text-grayteck-200">
                        {calculateDuration(entry)}
                      </span>
                    </div>
                  </div>

                  <div className="mt-1 flex items-center text-xs">
                    <User className="w-3 h-3 text-grayteck-500 dark:text-grayteck-400 mr-1" />
                    <span className="text-grayteck-500 dark:text-grayteck-400">
                      User: {entry.user_id}
                    </span>
                    {entry.billable && (
                      <>
                        <span className="mx-1 text-grayteck-400 dark:text-grayteck-500">•</span>
                        <DollarSign className="w-3 h-3 text-green-500 mr-1" />
                        <span className="text-green-600 dark:text-green-400">Billable</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TimeDetailsPanel;